import { useCallback } from "react"
import { useMutation } from "urql"

import type {
  DeleteCompanyProductAssociationMutation,
  DeleteCompanyProductAssociationMutationVariables,
} from "@/graphql/codegen/graphql"
import { DeleteCompanyProductAssociationDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseDeleteCompanyProductAssociationReturn {
  deleteAssociation: (id: string) => Promise<boolean>
  isDeletingAssociation: boolean
}

export const useDeleteCompanyProductAssociation = (): UseDeleteCompanyProductAssociationReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const [{ fetching }, executeMutation] = useMutation<
    DeleteCompanyProductAssociationMutation,
    DeleteCompanyProductAssociationMutationVariables
  >(DeleteCompanyProductAssociationDocument)

  const deleteAssociation = useCallback(
    async (id: string): Promise<boolean> => {
      const { data, error } = await executeMutation({ id })
      const output = data?.companyProductAssociation.delete

      const hasError = error || output?.__typename === "CompanyProductAssociationDeleteFailure"

      if (hasError) {
        const failureMessage =
          output?.__typename === "CompanyProductAssociationDeleteFailure" ? output.error.message : error?.message

        notify({
          type: NotificationType.Error,
          silent: false,
          message: failureMessage ?? "Failed to remove association. Please try again.",
        })
        return false
      }

      notify({
        type: NotificationType.Success,
        silent: false,
        message: "Association removed",
      })
      return true
    },
    [executeMutation, notify]
  )

  return { deleteAssociation, isDeletingAssociation: fetching }
}
